import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  LOCALE_ID,
  provideZoneChangeDetection
} from "@angular/core";
import { MAT_CARD_CONFIG } from "@angular/material/card";
import { MAT_RIPPLE_GLOBAL_OPTIONS } from "@angular/material/core";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter } from "@angular/router";
import { LabelLang } from "@ennova-research/waveup-common/enum";
import {
  getBrowserCultureLang,
  provideTransloco,
  TranslocoService
} from "@jsverse/transloco";
import { provideTranslocoLocale } from "@jsverse/transloco-locale";
import { provideTranslocoPersistLang } from "@jsverse/transloco-persist-lang";
import {
  popperVariation,
  provideTippyConfig,
  tooltipVariation,
  withContextMenuVariation
} from "@ngneat/helipopper";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { MarkdownModule, provideMarkdown } from "ngx-markdown";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { environment } from "../environments/environment";
import { resolveLanguage } from "./shared/utils/language/language.utils";
import { routes } from "./app.routes";
import { AuthInterceptor } from "./core/interceptors/auth/auth.interceptor";
import { i18nInterceptor } from "./core/interceptors/i18n/i18n.interceptor";
import { loaderInterceptor } from "./core/interceptors/loader/loader.interceptor";
import { SnackbarNotificationModule } from "./core/modules/snackbar-notification/snackbar-notification.module";
import { PaginatorIntl } from "./core/paginator-intl";
import { GlobalErrorHandlerService } from "./core/services/global-error-handler/global-error-handler.service";
import { TranslocoHttpLoader } from "./transloco-loader";

import { registerLocaleData } from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeIt from "@angular/common/locales/it";

registerLocaleData(localeIt, "it-IT");
registerLocaleData(localeEn, "en-US");

const TRANSLOCO_PERSIST_LANG_KEY = "translocoLang";

function initializeGoogleMaps(): () => Promise<void> {
  return () =>
    new Promise((resolve) => {
      const lang = getDefaultLanguage();
      const [language, region] = lang.split("-");
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&language=${language}&region=${region}`;
      script.async = true;
      script.defer = true;
      script.onload = (): void => resolve();
      script.onerror = (err): void => {
        console.error("Failed to load Google Maps API", err);
        resolve();
      };
      document.head.appendChild(script);
    });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    provideHttpClient(
      withInterceptors([loaderInterceptor, i18nInterceptor]),
      withInterceptorsFromDi()
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAnimationsAsync(),
    provideTransloco({
      config: {
        availableLangs: Object.values(LabelLang),
        defaultLang: getDefaultLanguage(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoLocale(),
    provideTranslocoPersistLang({
      storage: { useValue: localStorage },
      storageKey: TRANSLOCO_PERSIST_LANG_KEY
    }),
    {
      provide: LOCALE_ID,
      useFactory: (translocoService: TranslocoService) =>
        translocoService.getActiveLang(),
      deps: [TranslocoService]
    },
    importProvidersFrom(
      NgxSkeletonLoaderModule.forRoot({}),
      SnackbarNotificationModule.forRoot(),
      MarkdownModule.forRoot()
    ),
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
      deps: [TranslocoService]
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "outline" }
    },
    {
      provide: MAT_CARD_CONFIG,
      useValue: { appearance: "outlined" }
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: { disabled: true }
    },
    provideMarkdown(),
    provideTippyConfig({
      defaultVariation: "tooltip",
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
        contextMenu: withContextMenuVariation(popperVariation),
        menu: {
          ...popperVariation,
          appendTo: "parent",
          arrow: false,
          offset: [0, 0]
        }
      }
    }),
    provideCharts(withDefaultRegisterables()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGoogleMaps,
      multi: true
    }
  ]
};

function getDefaultLanguage(): LabelLang {
  return resolveLanguage(
    localStorage.getItem(TRANSLOCO_PERSIST_LANG_KEY),
    getBrowserCultureLang()
  );
}
